// ============================================================================
// Contracts — Authoring panel (Phase 2a, 2026-06-10).
// Renders the contract to a TCS PDF from the active contract_templates row
// via the esign Edge Function (`author_pdf`). The output is stored as the
// e-sign source document ({contract_id}/source.pdf), so Send for Signature
// needs no re-upload. Available pre-signature only.
// ============================================================================

import { canSendContracts } from '../../shared/role-gates';
import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { ErrorState } from '../../shared/components';
import { SUPABASE_ANON_KEY, getSupabaseToken } from '../../shared/supabase';
import { EsignError, esignInternal } from '../esign';
import type { ContractRow } from './types';
import { errorMessage } from '../../shared/errors';

const CONTRACT_SOURCE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contract-source-url`;

// Fetch a fresh signed URL for a contract's stored source PDF via the
// contract-source-url edge function. The `signed-contracts` bucket has no
// client storage policies, so a browser createSignedUrl can't do this — the
// function returns a service-role signed URL after checking the caller may
// see this contract.
async function fetchContractSourceUrl(contractId: string): Promise<string> {
  const jwt = getSupabaseToken();
  if (!jwt) throw new Error('You need to be signed in to open the PDF.');
  const res = await fetch(CONTRACT_SOURCE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ contract_id: contractId }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    url?: string;
    message?: string;
    error?: string;
  };
  if (!res.ok || !data.url) {
    throw new Error(
      data.message ?? data.error ?? `Couldn't open the PDF (${res.status}).`,
    );
  }
  return data.url;
}

const AUTHOR_STATUSES = [
  'Draft',
  'Pending_Finance',
  'Pending_Legal',
  'Pending_Executive',
  'Ready_for_Signature',
];

interface AuthoringPanelProps {
  contract: ContractRow;
  role: string | null;
  currentEid: string | null;
  onAfterAction: () => void;
}

interface AuthorResult {
  template_id: string;
  template_version: number;
  bytes: number;
  preview_url: string | null;
}

export function AuthoringPanel({
  contract,
  role,
  currentEid,
  onAfterAction,
}: AuthoringPanelProps) {
  const relevant = AUTHOR_STATUSES.includes(contract.status);
  const allowed =
    canSendContracts(role) ||
    (currentEid != null && contract.created_by_eid === currentEid);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuthorResult | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  if (!relevant || !allowed) return null;

  // Open the stored source PDF via a freshly-minted signed URL. Unlike the
  // transient `result.preview_url` (only present right after generation), this
  // works on every visit to a contract that already has a generated PDF — the
  // `signed-contracts` bucket is service-role-only, so the URL comes from the
  // esign function's read-only `source_url` action, not a client signed URL.
  async function handleViewPdf() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      const url = await fetchContractSourceUrl(contract.contract_id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setPdfError(
        errorMessage(err, 'Could not open the PDF.'),
      );
    } finally {
      setPdfBusy(false);
    }
  }

  async function handleGenerate() {
    setBusy(true);
    setError(null);
    try {
      const res = await esignInternal<AuthorResult>({
        action: 'author_pdf',
        contract_id: contract.contract_id,
      });
      setResult(res);
      onAfterAction();
    } catch (err) {
      if (err instanceof EsignError && err.code === 'no_template') {
        setError(
          `No active template exists for ${contract.contract_type.replace('_', ' ')} contracts yet. Templates live in Drive → _Standards and Templates → _Contracts → Templates.`,
        );
      } else {
        setError(errorMessage(err, 'PDF generation failed.'));
      }
    } finally {
      setBusy(false);
    }
  }

  const generatedAt = contract.authored_pdf_generated_at;

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <h3 className="mb-1 text-sm font-semibold text-tcs-text">
        Contract document
      </h3>
      <p className="text-[11px] leading-relaxed text-tcs-text-muted">
        Generate a TCS-formatted PDF from the {contract.contract_type.replace('_', ' ')}{' '}
        template using this contract's data. The PDF becomes the e-sign source
        document — re-generate any time before signature.
      </p>

      {(generatedAt || result) && (
        <p className="mt-2 text-[11px] text-tcs-text-secondary">
          {generatedAt && (
            <>
              Last generated {new Date(generatedAt).toLocaleString()}
              {contract.authored_from_template_id
                ? ` from ${contract.authored_from_template_id}`
                : ''}
              .{' '}
            </>
          )}
          <button
            type="button"
            onClick={handleViewPdf}
            disabled={pdfBusy}
            className="font-semibold text-tcs-accent-ink underline disabled:opacity-60"
          >
            {pdfBusy ? 'Opening…' : 'View / Download PDF'}
          </button>
        </p>
      )}

      {pdfError && (
        <p className="mt-1 text-[11px] text-rag-red">{pdfError}</p>
      )}

      {error && (
        <div className="mt-2">
          <ErrorState title="Couldn't generate the PDF" body={error} />
        </div>
      )}

      {result && (
        <div className="mt-2 rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2 text-[11px] text-st-ok-fg-strong">
          PDF generated from {result.template_id} (v{result.template_version},{' '}
          {Math.max(1, Math.round(result.bytes / 1024))} KB). Use the View /
          Download link above to open it.
        </div>
      )}

      <button
        type="button"
        onClick={handleGenerate}
        disabled={busy}
        className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
        {busy
          ? 'Generating…'
          : generatedAt || result
            ? 'Re-generate PDF'
            : 'Generate PDF from template'}
      </button>
    </div>
  );
}
